import {EMPTY, from, of} from 'rxjs';
import {combineEpics, ofType} from 'redux-observable';
import {catchError, filter, map, mergeMap, withLatestFrom} from 'rxjs/operators';
import {GatewayError} from '@yandex-data-ui/gateway';

import toaster from '~ui/utils/toaster';
import {i18n} from '~ui/components/search/i18n';
import {shouldShowError} from '~ui/utils';
import {WikiAction} from '~ui/store/types';
import {IS_MOBILE} from '~ui/constants';
import {sendParams} from '~ui/services/metrika';

import {WikiEpic} from '../../types';

import {actions} from './slice';
import {SearchQueryParams} from './types';
import {
    adaptFetchResultsResponse,
    getMetrikaDateType,
    getMetrikaDocumentType,
    getMetrikaSearchOrder,
    hasDateRange,
    toSearchArgs,
    updateLocation,
} from './helpers';

export const epic = combineEpics<WikiEpic>(
    (action$, state$) =>
        action$.pipe(
            ofType(
                actions.submitSearch.type,
                actions.changeOrder.type,
                actions.changeAuthorFilter.type,
                actions.changeDateFilter.type,
                actions.changeDateTypeFilter.type,
                actions.changeDocumentTypeFilter.type,
                actions.resetFilters.type,
                actions.setShowObsolete.type,
                actions.selectSlugFilter.type,
            ),
            withLatestFrom(state$),
            filter(([action, state]) => {
                return filterInvalidDateTypeEffects(action.type, state.search.params.date);
            }),
            map(() => actions.resetPage()),
        ),

    (action$, state$) =>
        action$.pipe(
            ofType(
                actions.submitSearch.type,
                actions.changePage.type,
                actions.changeOrder.type,
                actions.changeAuthorFilter.type,
                actions.changeDateFilter.type,
                actions.changeDateTypeFilter.type,
                actions.changeDocumentTypeFilter.type,
                actions.setShowObsolete.type,
                actions.resetFilters.type,
                actions.selectSlugFilter.type,
            ),
            withLatestFrom(state$),
            map(([, state]) => state.search),
            mergeMap(({params}) => {
                updateLocation(params);

                return EMPTY;
            }),
        ),

    (action$, state$) =>
        action$.pipe(
            ofType(
                actions.init.type,
                actions.submitSearch.type,
                actions.changePage.type,
                actions.changeOrder.type,
                actions.changeAuthorFilter.type,
                actions.changeDateFilter.type,
                actions.changeDateTypeFilter.type,
                actions.changeDocumentTypeFilter.type,
                actions.resetFilters.type,
                actions.setShowObsolete.type,
                actions.selectSlugFilter.type,
            ),
            withLatestFrom(state$),
            filter(([action, state]) => {
                return filterInvalidDateTypeEffects(action.type, state.search.params.date);
            }),
            mergeMap(([{type}]) => {
                const result: WikiAction[] = [actions.fetchResults()];

                // we have a different loader for changePage action
                if (type !== actions.changePage.type) {
                    result.push(actions.setSearchLoading());
                }

                return result;
            }),
        ),

    (action$, state$, {sdk}) =>
        action$.pipe(
            ofType(actions.fetchResults.type),
            withLatestFrom(state$),
            map(([, state]) => state.search),
            mergeMap(({params}) =>
                from(sdk.wiki.search(toSearchArgs(params))).pipe(
                    map((data) => adaptFetchResultsResponse(data)),
                    map((data) => actions.fetchResultsSuccess(data)),
                    catchError((error: GatewayError) => {
                        if (shouldShowError(error)) {
                            toaster.createErrorToast(
                                actions.fetchResults.type,
                                i18n('search-error'),
                                error,
                            );
                        }

                        return of(actions.fetchResultsFailed(error));
                    }),
                ),
            ),
        ),

    (action$, state$, {sdk}) =>
        action$.pipe(
            ofType(actions.fetchNextChunk.type),
            withLatestFrom(state$),
            map(([, state]) => state.search),
            mergeMap(({params}) =>
                from(sdk.wiki.search(toSearchArgs(params))).pipe(
                    map((data) => adaptFetchResultsResponse(data)),
                    map((data) => actions.fetchNextChunkSuccess(data)),
                    catchError((error) => {
                        toaster.createErrorToast(
                            actions.fetchNextChunk.type,
                            i18n('search-error'),
                            error,
                        );

                        return of(actions.fetchResultsFailed(error));
                    }),
                ),
            ),
        ),

    (action$, state$) =>
        action$.pipe(
            ofType(
                actions.init.type,
                actions.submitSearch.type,
                actions.changeOrder.type,
                actions.resetFilters.type,
                actions.changeAuthorFilter.type,
                actions.changeDateFilter.type,
                actions.changeDateTypeFilter.type,
                actions.changeDocumentTypeFilter.type,
                actions.setShowObsolete.type,
                actions.selectSlugFilter.type,
            ),
            withLatestFrom(state$),
            filter(([action, state]) => {
                return filterInvalidDateTypeEffects(action.type, state.search.params.date);
            }),
            mergeMap(([action, state]) => {
                const {date, order, documentType, text} = state.search.params;

                switch (action.type) {
                    case actions.changeOrder.type:
                        sendParams('Поиск', 'Сортировка', getMetrikaSearchOrder(order));
                        break;
                    case actions.resetFilters.type:
                        sendParams('Поиск', 'Фильтры', 'Очистить_Все');
                        break;
                    case actions.changeAuthorFilter.type:
                        sendParams('Поиск', 'Фильтры', 'Автор');
                        break;
                    case actions.changeDateFilter.type:
                    case actions.changeDateTypeFilter.type:
                        const dateType = date && getMetrikaDateType(date.type);
                        if (dateType) {
                            sendParams('Поиск', 'Фильтры', dateType);
                        }
                        break;
                    case actions.changeDocumentTypeFilter.type:
                        sendParams(
                            'Поиск',
                            'Фильтры',
                            'Тип_Документа',
                            getMetrikaDocumentType(documentType),
                        );
                        break;
                    case actions.setShowObsolete.type:
                        const {showObsolete} = state.search.params;
                        sendParams(
                            'Поиск',
                            'Фильтры',
                            'Показать_Устаревшие',
                            showObsolete ? 'Да' : 'Нет',
                        );
                        break;
                    case actions.selectSlugFilter.type:
                        sendParams('Поиск', 'Фильтры', 'Раздел');
                        break;
                }

                if (action.type !== actions.init.type || text.length > 0) {
                    const postfix = IS_MOBILE ? '_Моб' : '';
                    sendParams('Поиск', 'Поисковый_Запрос' + postfix);
                }

                return EMPTY;
            }),
        ),
);

function filterInvalidDateTypeEffects(type: string, date: SearchQueryParams['date']) {
    if (type !== actions.changeDateTypeFilter.type) return true;
    if (hasDateRange(date)) return true;
    return false;
}
